// Shared constants and tiny presentational helpers for the Expense Audit pages.
// Pulled out of the monolithic ExpenseAudit.jsx in iter 14 (Phase G refactor).
import React from 'react';
import { moneyFull } from '../../lib/api';

export const STATUS_COLORS = {
  Keep: { bg: 'bg-indie-evergreen/15', text: 'text-indie-evergreen', dot: 'bg-indie-evergreen' },
  'Right-size': { bg: 'bg-indie-yellow/30', text: 'text-indie-ink', dot: 'bg-indie-yellow' },
  Renegotiate: { bg: 'bg-indie-cobalt/15', text: 'text-indie-cobalt', dot: 'bg-indie-cobalt' },
  Cut: { bg: 'bg-indie-neg/15', text: 'text-indie-neg-dark', dot: 'bg-indie-neg-dark' },
  Cancel: { bg: 'bg-indie-neg/15', text: 'text-indie-neg-dark', dot: 'bg-indie-neg-dark' },
  Watch: { bg: 'bg-indie-ink/10', text: 'text-indie-ink/65', dot: 'bg-indie-ink/40' },
  'Review Fit': { bg: 'bg-indie-cobalt/15', text: 'text-indie-cobalt', dot: 'bg-indie-cobalt' },
  'CAC Review Required': { bg: 'bg-indie-yellow/30', text: 'text-indie-ink', dot: 'bg-indie-yellow' },
  'Pending Review': { bg: 'bg-indie-yellow/40', text: 'text-indie-ink', dot: 'bg-indie-yellow' },
  Unmapped: { bg: 'bg-indie-neg/15', text: 'text-indie-neg-dark', dot: 'bg-indie-neg-dark' },
};

export const DECISION_OPTIONS = ['Keep', 'Right-size', 'Renegotiate', 'Cut', 'Cancel', 'Watch', 'Review Fit', 'CAC Review Required', 'Pending Review'];

// Statuses that REQUIRE a reviewer-entered target before Opp/mo is computed.
// Mirrors backend `ACTIONABLE_STATUSES` in expense_audit_integration.py.
export const ACTIONABLE_STATUSES = ['Right-size', 'Renegotiate', 'Cancel', 'Cut'];

export const PASSTHROUGH_LABELS = {
  payroll: 'Payroll',
  trust_passthrough: 'Trust passthrough',
  owner_draw: 'Owner draw',
  bank_transfer: 'Bank transfer',
  banking_fee: 'Banking fee',
  statement_payoff: 'Statement payoff',
  refund_or_inflow: 'Refund / inflow',
  government_fee: 'Government fee',
  trust_interest: 'Trust interest',
  other: 'Other',
};

// "Joey Vitale" → "JV". Falls back to email prefix when the name is missing.
export function autoInitials(user) {
  const name = (user?.name || '').trim();
  if (name) return name.split(/\s+/).map((p) => p[0]).join('').slice(0, 4).toUpperCase();
  return (user?.email || '').slice(0, 2).toUpperCase();
}

// Format a vendor row's spend depending on whether it recurs monthly, fires
// once, or hits sporadically. Returns {primary, secondary} for two-line display.
export function formatSpend(row) {
  const cadence = row?.cadence;
  if (!row || !row.total_amount) return { primary: '—', secondary: '' };
  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return iso; }
  };
  if (cadence === 'recurring' && row.monthly_run_rate) {
    return {
      primary: `${moneyFull(row.monthly_run_rate)}/mo`,
      secondary: `${row.txn_count} txns · ${row.months_active} mo active`,
    };
  }
  if (cadence === 'one_time') {
    return {
      primary: `${moneyFull(row.total_amount)} once`,
      secondary: row.last_date_iso ? `on ${fmtDate(row.last_date_iso)}` : '1 txn',
    };
  }
  return {
    primary: `${moneyFull(row.total_amount)} total`,
    secondary: `${row.txn_count} hits · last ${fmtDate(row.last_date_iso)}`,
  };
}

export const CHART_GRID = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
export const CHART_AXIS = { stroke: '#141B4D', strokeOpacity: 0.3 };
export const CHART_TOOLTIP = { background: '#FFFFFF', border: '1px solid #141B4D', color: '#141B4D', borderRadius: 0, fontSize: 12 };

export function Th({ children, onClick, active, dir, right }) {
  return (
    <th
      onClick={onClick}
      className={`px-3 py-2 text-[10px] uppercase tracking-wider font-medium ${right ? 'text-right' : 'text-left'} ${onClick ? 'cursor-pointer hover:bg-indie-cobalt/40' : ''}`}
    >
      {children} {active && (dir === 'asc' ? '↑' : '↓')}
    </th>
  );
}

export function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-indie-cream border border-indie-navy/15 px-2 py-1 text-xs focus:outline-none focus:border-indie-navy"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

export function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-indie-ink/55">{label}</div>
      <div className="text-indie-navy font-semibold num-tabular">{value}</div>
    </div>
  );
}

export function ScoreBox({ label, v }) {
  const tone = v == null
    ? 'bg-indie-ink/10 text-indie-ink/45'
    : v <= 2 ? 'bg-indie-neg/15 text-indie-neg-dark'
    : v >= 4 ? 'bg-indie-evergreen/15 text-indie-evergreen'
    : 'bg-indie-yellow/20 text-indie-ink';
  return (
    <div className={`text-center py-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
      <div className="text-base font-bold num-tabular">{v ?? '—'}<span className="text-[10px] font-normal opacity-60">/5</span></div>
    </div>
  );
}
